import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MessageSquare, Mail, Phone, Check, CheckCheck, Eye, CreditCard, AlertCircle } from 'lucide-react';

interface InvoiceEvent {
  id: string;
  event_type: 'sent' | 'delivered' | 'viewed' | 'clicked' | 'paid' | 'failed';
  created_at: string;
  channel: 'whatsapp' | 'email' | 'sms' | null;
  metadata: Record<string, unknown> | null;
  invoice_id: string;
}

interface ClienteHistoricoTabProps {
  customerId: string;
}

const channelIcons: Record<string, React.ElementType> = {
  whatsapp: MessageSquare,
  email: Mail,
  sms: Phone
};

const channelColors: Record<string, string> = {
  whatsapp: 'bg-[hsl(142,70%,45%)]',
  email: 'bg-[hsl(var(--basic-500))]',
  sms: 'bg-[hsl(var(--accent-500))]'
};

const channelLabels: Record<string, string> = {
  whatsapp: 'WhatsApp',
  email: 'Email',
  sms: 'SMS'
};

const eventIcons: Record<string, React.ElementType> = {
  sent: Check,
  delivered: CheckCheck,
  viewed: Eye,
  clicked: Eye,
  paid: CreditCard,
  failed: AlertCircle
};

const eventLabels: Record<string, string> = {
  sent: 'Cobrança enviada',
  delivered: 'Cobrança entregue',
  viewed: 'Cobrança visualizada',
  clicked: 'Página de pagamento aberta',
  paid: 'Pagamento recebido',
  failed: 'Falha no envio'
};

const eventColors: Record<string, string> = {
  sent: 'text-[hsl(var(--basic-700))]',
  delivered: 'text-[hsl(142,70%,35%)]',
  viewed: 'text-[hsl(142,70%,35%)]',
  clicked: 'text-[hsl(var(--accent-500))]',
  paid: 'text-[hsl(142,70%,35%)]',
  failed: 'text-[hsl(var(--careful-500))]'
};

function groupEventsByDateAndChannel(events: InvoiceEvent[]) {
  const groups: Record<string, { channel: string; events: InvoiceEvent[] }[]> = {};
  
  events.forEach(event => {
    const dateKey = new Date(event.created_at).toLocaleDateString('pt-BR');
    const channel = event.channel || 'system';
    
    if (!groups[dateKey]) groups[dateKey] = [];
    
    // Find or create channel group for this date
    let channelGroup = groups[dateKey].find(g => g.channel === channel);
    if (!channelGroup) {
      channelGroup = { channel, events: [] };
      groups[dateKey].push(channelGroup);
    }
    
    channelGroup.events.push(event);
  });
  
  // Sort events within each channel group
  Object.values(groups).forEach(dateGroup => {
    dateGroup.forEach(channelGroup => {
      channelGroup.events.sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });
  });
  
  return groups;
}

export function ClienteHistoricoTab({ customerId }: ClienteHistoricoTabProps) {
  const { data: events = [], isLoading } = useQuery({
    queryKey: ['customer-history', customerId],
    queryFn: async () => {
      // First get all invoices for this customer
      const { data: invoices } = await supabase
        .from('invoices')
        .select('id')
        .eq('customer_id', customerId);
      
      if (!invoices?.length) return [];

      // Then get all events for these invoices
      const { data, error } = await supabase
        .from('invoice_events')
        .select('*')
        .in('invoice_id', invoices.map(i => i.id))
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data as InvoiceEvent[];
    }
  });

  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground">Carregando...</div>;
  }

  if (events.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground italic bg-muted/30 rounded-lg">
        Nenhuma movimentação registrada.
      </div>
    );
  }

  const groupedEvents = groupEventsByDateAndChannel(events);
  const sortedDates = Object.keys(groupedEvents).sort((a, b) => {
    const [dayA, monthA, yearA] = a.split('/').map(Number);
    const [dayB, monthB, yearB] = b.split('/').map(Number);
    return new Date(yearB, monthB - 1, dayB).getTime() - new Date(yearA, monthA - 1, dayA).getTime();
  });

  return (
    <div className="space-y-4">
      <h3 className="font-bold text-[hsl(var(--basic-900))] uppercase text-sm tracking-wider">
        Histórico
      </h3>
      
      <div className="space-y-4">
        {sortedDates.map(date => (
          <div key={date} className="space-y-3">
            {groupedEvents[date].map((channelGroup, idx) => {
              const Icon = channelIcons[channelGroup.channel] || MessageSquare;
              const bgColor = channelColors[channelGroup.channel] || 'bg-muted';
              const label = channelLabels[channelGroup.channel] || 'Sistema';
              
              // Check if this channel group has a paid event
              const hasPaidEvent = channelGroup.events.some(e => e.event_type === 'paid');
              
              return (
                <div 
                  key={`${date}-${channelGroup.channel}-${idx}`} 
                  className={`rounded-xl border overflow-hidden ${hasPaidEvent ? 'border-[hsl(142,70%,45%)] bg-[hsl(142,70%,97%)]' : 'bg-card'}`}
                >
                  {/* Paid Event Banner */}
                  {hasPaidEvent && (
                    <div className="bg-[hsl(142,70%,45%)] text-white px-4 py-2.5 flex items-center gap-2 font-bold">
                      <CreditCard className="h-4 w-4" />
                      Pagamento recebido
                    </div>
                  )}
                  
                  {/* Channel Header */}
                  <div className="flex items-center gap-3 px-4 py-3 border-b bg-muted/30">
                    <div className={`h-8 w-8 rounded-lg ${bgColor} flex items-center justify-center`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <span className="font-bold text-[hsl(var(--basic-900))]">{label}</span>
                      <span className="ml-2 text-sm text-muted-foreground">{date}</span>
                    </div>
                  </div>
                  
                  {/* Events Timeline */}
                  <div className="divide-y">
                    {channelGroup.events
                      .filter(e => e.event_type !== 'paid') // Don't show paid in timeline, it's in banner
                      .map((event) => {
                        const EventIcon = eventIcons[event.event_type] || Check;
                        const time = new Date(event.created_at).toLocaleString('pt-BR');
                        const colorClass = eventColors[event.event_type] || 'text-muted-foreground';
                        
                        return (
                          <div 
                            key={event.id} 
                            className="flex items-center gap-4 px-4 py-3 hover:bg-muted/20 transition-colors"
                          >
                            <span className="text-xs text-muted-foreground font-mono min-w-[140px]">
                              {time}
                            </span>
                            <EventIcon className={`h-4 w-4 ${colorClass}`} />
                            <span className={`text-sm ${colorClass}`}>
                              {eventLabels[event.event_type]}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
